import "./UpdateProfile.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import Modal from "../../Components/Modal/Modal";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import Select from "react-select";
import VivahApi from "../../api";
import Profile from "../../api/Profile";
import moment from "moment";
import { sanitizeProfileUpdate } from "../../utils/api-utils";
import { setLoadingMessage } from "../../utils/loading-store";

// Default styles for react-select controls to match signup rounded inputs
const selectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: "8px",
    minHeight: "44px",
    border: "1.5px solid #d1d5db",
    boxShadow: "none",
  }),
  control: (provided, state) => ({
    ...provided,
    borderRadius: "8px",
    minHeight: "44px",
    border: state.isFocused ? "1.5px solid #e50d7e" : "1.5px solid #d1d5db",
    boxShadow: "none",
  }),
};

// Apply defaults globally for this module
Select.defaultProps = {
  styles: selectStyles,
  classNamePrefix: "react-select",
};

function UpdateProfile() {
  const { register, handleSubmit, formState, control, setValue, watch, reset, trigger } =
    useForm();

  const watchedValue = watch();

  const genders = [
    { value: "M", label: "Male" },
    { value: "F", label: "Female" },
    { value: "T", label: "Transgender" },
  ];

  // const options = [];
  const [profile, setProfile] = useState({});
  const [casteId, setCasteID] = useState([]);
  const [gothraId, setGothraID] = useState([]);
  const [cityId, setCityID] = useState([]);
  const [step, setStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [modal, setModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
  });

  useEffect(() => {
    const init = async () => {
      const res = await VivahApi.profile.listProfile();
      setProfile(res.data);
      
      const userProfileData = await VivahApi.Auth.getUserProfileUpdateData();
      
      // API returns: { user: { ...all fields }, auth: { ... } }
      // Form expects: { profile: { religionId, casteId, etc. }, user: { firstName, lastName, dateOfBirth } }
      const userData = userProfileData.data.user || {};
      
      // Basic user fields
      const user = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        dateOfBirth: userData.dateOfBirth ? moment(userData.dateOfBirth).format("YYYY-MM-DD") : '',
        gender: userData.gender,
        email: userData.email,
        phone: userData.phoneNumber || userData.phone
      };
      
      // Profile fields (everything else from user data)
      // Parse ID fields to integers to match master data types
      const parseId = (value) => value ? parseInt(value) : null;
      
      const profile = {
        ...userData, // Spread first to get all base fields
        sid: userData.userId,
        // Then override ID fields with parsed integers
        religionId: parseId(userData.religionId),
        casteId: parseId(userData.casteId),
        gothraId: parseId(userData.gothraId),
        motherTongueId: parseId(userData.motherTongueId),
        stateId: parseId(userData.stateId),
        cityId: parseId(userData.cityId),
        heightId: parseId(userData.heightId),
        maritalStatusId: parseId(userData.maritalStatusId),
        professionId: parseId(userData.professionId),
        professionTypeId: parseId(userData.professionTypeId),
        qualificationId: parseId(userData.qualificationId),
        bloodGroupId: parseId(userData.bloodGroupId),
        bodyTypeId: parseId(userData.bodyTypeId),
        dietId: parseId(userData.dietId),
        healthInformationId: parseId(userData.healthInformationId),
        disabilityId: parseId(userData.disabilityId),
        familyStatusId: parseId(userData.familyStatusId),
        familyValuesId: parseId(userData.familyValuesId),
        fatherOccupationId: parseId(userData.fatherOccupationId),
        motherOccupationId: parseId(userData.motherOccupationId),
        incomeId: parseId(userData.incomeId),
        familyStateId: parseId(userData.familyStateId),
        familyCityId: parseId(userData.familyCityId)
      };
      reset({ profile, user });
    };
    init();
  }, []);

  // console.log('watchedValue', watchedValue);

  const stepFieldMap = {
    1: [
      "user.firstName",
      "user.lastName",
      "user.dateOfBirth",
      "profile.gender",
      "user.email",
      "user.phone",
      "profile.religionId",
      "profile.casteId",
      "profile.motherTongueId",
    ],
    2: [
      "profile.stateId",
      "profile.cityId",
      "profile.address",
      "profile.heightId",
      "profile.maritalStatusId",
      "profile.dietId",
    ],
    3: [
      "profile.qualificationId",
      "profile.professionTypeId",
      "profile.professionId",
      "profile.companyName",
      "profile.incomeId",
    ],
    4: ["profile.aboutYourself"],
  };

  const stepFieldLabels = {
    1: [
      "First Name",
      "Last Name",
      "Date of Birth",
      "Gender",
      "Email",
      "Mobile",
      "Religion",
      "Caste",
      "Mother Tongue",
    ],
    2: [
      "State",
      "City",
      "Address",
      "Height",
      "Marital Status",
      "Diet",
    ],
    3: ["Qualification", "Work Type", "Profession", "Company", "Income"],
    4: ["About Yourself"],
  };

  const nextStep = async () => {
    const fields = stepFieldMap[step] || [];
    let valid = false;
    try {
      valid = await trigger(fields);
    } catch (e) {
      valid = false;
    }

    // Fallback simple required-field check if react-hook-form has no rules
    if (!valid) {
      const isFilled = (path) => {
        try {
          const parts = path.split(".");
          let cur = watchedValue;
          for (const p of parts) {
            if (!cur) return false;
            cur = cur[p];
          }
          return cur !== undefined && cur !== null && String(cur).trim() !== "" && !(Array.isArray(cur) && cur.length===0);
        } catch (e) {
          return false;
        }
      };
      const missing = fields.find((f) => !isFilled(f));
      if (!missing) valid = true;
    }

    if (valid) {
      setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
      setStep((s) => s + 1);
    }
    else {
      setModal({
        isOpen: true,
        type: "error",
        title: "Validation",
        message: "Please complete the required fields before continuing.",
      });
    }
  };

  const handleStepClick = (target) => {
    if (target === step) return;
    setStep(target);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const showGothra = () => {
    const HinduId =
      profile.religions && profile.religions.find((e) => e.name == "Hindu");
    // console.log(HinduId);
    // console.log(watchedValue["religionId"]);
    return (
      HinduId &&
      watchedValue["profile"] &&
      HinduId.sid == watchedValue["profile"]["religionId"]
    );
  };

  const onSubmit = async (data) => {
    try {
      if (!showGothra()) {
        data.profile.gothraId = 0;
      }
      
      const resprofile = sanitizeProfileUpdate(data.profile);
      setLoadingMessage("Saving changes...");
      const response = await VivahApi.Auth.updateProfile(resprofile);
      
      const successMessage =
        response?.data?.message || "Profile updated successfully.";
      setModal({
        isOpen: true,
        type: "success",
        title: "Profile Updated",
        message: successMessage,
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Profile update failed.";
      setModal({
        isOpen: true,
        type: "error",
        title: "Update Failed",
        message: errorMessage,
      });
    }
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const getSelectedReligion = () => {
    return (
      profile.religions &&
      watchedValue["profile"] &&
      profile.religions.find(
        (e) => e.sid === watchedValue["profile"]["religionId"]
      )
    );
  };

  const getSelectedState = () => {
    return (
      profile.states &&
      watchedValue["profile"] &&
      profile.states.find((e) => e.sid === watchedValue["profile"]["stateId"])
    );
  };

  const getSelecteFamilyState = () => {
    return (
      profile.states &&
      watchedValue["profile"] &&
      profile.states.find(
        (e) => e.sid === watchedValue["profile"]["familyStateId"]
      )
    );
  };
  return (
    <>
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText="OK"
      />
      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />
        <div
          className="gla_page_title gla_image_bck gla_wht_txt"
          data-color="#282828"
        >
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">Update Profile</h1>
              </div>
              <div className="col-md-4">
                <div className="breadcrumbs">
                  <a href="#">Home</a>
                  <span>Update Profile</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <section id="gla_content" className="gla_content">
          <div className="container mb-3">
            <div className="update-stepper">
                <div
                  className={`step ${step === 1 ? 'active' : ''} clickable`}
                  title={stepFieldLabels[1].join(', ')}
                  onClick={() => handleStepClick(1)}
                >
                  1 • Basic
                </div>
                <div
                  className={`step ${step === 2 ? 'active' : ''} clickable`}
                  title={stepFieldLabels[2].join(', ')}
                  onClick={() => handleStepClick(2)}
                >
                  2 • Family
                </div>
                <div
                  className={`step ${step === 3 ? 'active' : ''} clickable`}
                  title={stepFieldLabels[3].join(', ')}
                  onClick={() => handleStepClick(3)}
                >
                  3 • Career
                </div>
                <div
                  className={`step ${step === 4 ? 'active' : ''} clickable`}
                  title={stepFieldLabels[4].join(', ')}
                  onClick={() => handleStepClick(4)}
                >
                  4 • About
                </div>
                <div className="progress-line">
                <div
                  className="progress-fill"
                  style={{ width: `${((step - 1) / 3) * 100}%` }}
                />
                </div>
              </div>
          </div>
          <section className="gla_section">
            <div className="update-form-wrap">
              <div className="update-form-card">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    {step === 1 && (
                      <>
                        <h3 id="basic">Basic Information</h3>
                        <div className="row">
                          <div className="col-md-6">
                            <label>Your First Name*</label>
                            <input
                              type="text"
                              disabled
                              {...register("user.firstName")}
                              name="firstName"
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Your Last Name*</label>
                            <input
                              type="text"
                              disabled
                              {...register("user.lastName")}
                              name="lastName"
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label>Date of Birth*</label>
                            <input
                              type="date"
                              disabled
                              {...register("user.dateOfBirth")}
                              name="dateOfBirth"
                              onChange={(e) => console.log(e.target.value)}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            {/* <label>Gender*</label>
                                                <Select options={gender}
                                                    value={(gender) && gender.find(e => e.value === watchedValue["gender"])}
                                                /> */}
                            <label><span style={{color: 'red'}}>*</span> Gender</label>
                            <Controller
                              control={control}
                              name="gender"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={genders}
                                  getOptionLabel={(e) => e.label}
                                  getOptionValue={(e) => e.value}
                                  value={
                                    watchedValue["profile"] &&
                                    genders.find(
                                      (e) =>
                                        e.value ===
                                        watchedValue["profile"]["gender"]
                                    )
                                  }
                                  onChange={(val) => {
                                    //console.log(val);
                                    setValue("profile.gender", val.value);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label>Your e-mail*</label>
                            <input
                              disabled
                              type="text"
                              {...register("user.email")}
                              name="email"
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Your Mobile*</label>
                            <input
                              type="text"
                              disabled
                              name="phone"
                              {...register("user.phone")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> Religion</label>
                            <Controller
                              control={control}
                              name="religionId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.religions}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={getSelectedReligion()}
                                  onChange={(val) => {
                                    // console.log(val);
                                    setValue("profile.religionId", val.sid);
                                    setValue("profile.casteId", -1);
                                    // console.log('caste', val.castes)
                                    setCasteID(val.castes);
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> Caste</label>
                            <Controller
                              control={control}
                              name="casteId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={
                                    getSelectedReligion() &&
                                    getSelectedReligion().castes
                                  }
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    watchedValue["profile"] &&
                                    getSelectedReligion() &&
                                    getSelectedReligion().castes.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["casteId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    // console.log(val);
                                    setValue("profile.casteId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> Mother Tongue</label>
                            <Controller
                              control={control}
                              name="motherTongueId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.motherTongues}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.motherTongues &&
                                    watchedValue["profile"] &&
                                    profile.motherTongues.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "motherTongueId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    //console.log(val);
                                    setValue("profile.motherTongueId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                          {showGothra() && (
                            <div className="col-md-6">
                              <label>Gothra/Gothram*</label>
                              <Controller
                                control={control}
                                name="gothraId"
                                render={({ onChange, value, name, ref }) => (
                                  <Select
                                    inputRef={ref}
                                    options={profile.gothra}
                                    getOptionLabel={(e) => e.name}
                                    getOptionValue={(e) => e.sid}
                                    value={
                                      profile.gothra &&
                                      watchedValue["profile"] &&
                                      profile.gothra.find(
                                        (e) =>
                                          e.sid ===
                                          watchedValue["profile"]["gothraId"]
                                      )
                                    }
                                    onChange={(val) => {
                                      setValue("profile.gothraId", val.sid);
                                    }}
                                  />
                                )}
                              />
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {step === 2 && (
                      <>
                        <h3 id="family">Family Information</h3>
                        <div className="row">
                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> State You Live In</label>
                            <Controller
                              control={control}
                              name="stateId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.states}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={getSelectedState()}
                                  onChange={(val) => {
                                    //console.log(val);
                                    setValue("profile.stateId", val.sid);
                                    setCityID(val.cities);
                                  }}
                                />
                              )}
                            />
                          </div>

                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> City You Live In</label>
                            <Controller
                              control={control}
                              name="cityId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={
                                    getSelectedState() &&
                                    getSelectedState().cities
                                  }
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    watchedValue["profile"] &&
                                    getSelectedState() &&
                                    getSelectedState().cities.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["cityId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.cityId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label>Full Address*</label>
                            <textarea
                              name="address"
                              {...register("profile.address")}
                              className="form-control form-opacity border-radius-0"
                            ></textarea>
                          </div>
                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> Height</label>
                            <Controller
                              control={control}
                              name="heightId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.heights}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.heights &&
                                    watchedValue["profile"] &&
                                    profile.heights.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["heightId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.heightId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12">
                            <label>You Live With Your Family*</label>
                            <input
                              type="radio"
                              {...register("profile.withFamily")}
                              value="no"
                            />
                            <span>No</span>
                            <input
                              type="radio"
                              {...register("profile.withFamily")}
                              value="yes"
                            />
                            <span>Yes</span>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label>State You Family Live In*</label>
                            <Controller
                              control={control}
                              name="familyStateId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.states}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={getSelecteFamilyState()}
                                  onChange={(val) => {
                                    //console.log(val);
                                    setValue("profile.familyStateId", val.sid);
                                    setCityID(val.cities);
                                  }}
                                />
                              )}
                            />
                          </div>

                          <div className="col-md-6">
                            <label>City Your Family Live In *</label>
                            <Controller
                              control={control}
                              name="familyCityId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={
                                    getSelecteFamilyState() &&
                                    getSelecteFamilyState().cities
                                  }
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    watchedValue["profile"] &&
                                    getSelecteFamilyState() &&
                                    getSelecteFamilyState().cities.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["familyCityId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.familyCityId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12">
                            <label><span style={{color: 'red'}}>*</span> Marital Status</label>
                            <Controller
                              control={control}
                              name="maritalStatusId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.maritalStatuses}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.maritalStatuses &&
                                    watchedValue["profile"] &&
                                    profile.maritalStatuses.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "maritalStatusId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue(
                                      "profile.maritalStatusId",
                                      val.sid
                                    );
                                  }}
                                />
                              )}
                            />
                            {/* <input type="radio" {...register('profile.maritalStatus')} value="Never Married" />
                                                        <span>Never Married</span>
                                                        <input type="radio" {...register('profile.maritalStatus')} value="Widow or Widower" />
                                                        <span>Widow or Widower</span>
                                                        <input type="radio" {...register('profile.maritalStatus')} value="Divorced" />
                                                        <span>Divorced</span>
                                                        <input type="radio" {...register('profile.maritalStatus')} value="Awaiting Divorce" />
                                                        <span>Awaiting Divorce</span> */}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12">
                            <label><span style={{color: 'red'}}>*</span> Your Diet</label>
                            <Controller
                              control={control}
                              name="dietId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.diets}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.diets &&
                                    watchedValue["profile"] &&
                                    profile.diets.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["dietId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.dietId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {step === 3 && (
                      <>
                        <h3 id="education">Education & Career</h3>
                        <div className="row">
                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> Your highest qualification</label>
                            <Controller
                              control={control}
                              name="qualificationId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.qualifications}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.qualifications &&
                                    watchedValue["profile"] &&
                                    profile.qualifications.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "qualificationId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue(
                                      "profile.qualificationId",
                                      val.sid
                                    );
                                  }}
                                />
                              )}
                            />
                          </div>

                          <div className="col-md-6">
                            <label>Your College Name(Highest Degree)*</label>
                            <input
                              type="text"
                              {...register("profile.collegeName")}
                              className={`form-control form-opacity border-radius-0 `}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> You Work With</label>
                            <Controller
                              control={control}
                              name="professionTypeId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.professionTypes}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.professionTypes &&
                                    watchedValue["profile"] &&
                                    profile.professionTypes.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "professionTypeId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue(
                                      "profile.professionTypeId",
                                      val.sid
                                    );
                                  }}
                                />
                              )}
                            />
                          </div>

                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> Working As</label>
                            <Controller
                              control={control}
                              name="professionId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.professions}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.professions &&
                                    watchedValue["profile"] &&
                                    profile.professions.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["professionId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.professionId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label>Your Company Name (Current)*</label>
                            <input
                              type="text"
                              {...register("profile.companyName")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>

                          <div className="col-md-6">
                            <label><span style={{color: 'red'}}>*</span> Income (Yearly)</label>
                            <Controller
                              control={control}
                              name="incomeId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.incomes}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.incomes &&
                                    watchedValue["profile"] &&
                                    profile.incomes.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["incomeId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.incomeId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {step === 4 && (
                      <>
                        <h3 id="about">About You</h3>
                        <div className="row">
                          <div className="col-md-12">
                            <label>About Yourself *</label>
                            <textarea
                              name="about_yourself"
                              {...register("profile.aboutYourself")}
                              className="form-control form-opacity border-radius-4"
                            ></textarea>
                          </div>
                          <div className="col-md-6">
                            <label>Body Weight</label>
                            <input
                              type="text"
                              {...register("profile.bodyWeight")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Body Type</label>
                            <Controller
                              control={control}
                              name="bodyTypeId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.bodyTypes}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.bodyTypes &&
                                    watchedValue["profile"] &&
                                    profile.bodyTypes.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["bodyTypeId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.bodyTypeId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Health Information</label>
                            <Controller
                              control={control}
                              name="healthInformationId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.healthInfo}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.healthInfo &&
                                    watchedValue["profile"] &&
                                    profile.healthInfo.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "healthInformationId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue(
                                      "profile.healthInformationId",
                                      val.sid
                                    );
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Disability</label>
                            <Controller
                              control={control}
                              name="disabilityId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.disabilities}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.disabilities &&
                                    watchedValue["profile"] &&
                                    profile.disabilities.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["disabilityId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.disabilityId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Complexion</label>
                            <input
                              type="text"
                              {...register("profile.complexion")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Blood Group</label>
                            <Controller
                              control={control}
                              name="bloodGroupId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.bloodGroups}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.bloodGroups &&
                                    watchedValue["profile"] &&
                                    profile.bloodGroups.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["bloodGroupId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.bloodGroupId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Birth Place</label>
                            <input
                              type="text"
                              {...register("profile.birthPlace")}
                                className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Birth Time</label>
                            <input
                              type="text"
                              {...register("profile.birthTime")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Father Name </label>
                            <input
                              type="text"
                              {...register("profile.fatherName")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Mother Name </label>
                            <input
                              type="text"
                              {...register("profile.motherName")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>No of Brother</label>
                            <input
                              type="text"
                              {...register("profile.brother")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          {/* <div className="col-md-6">
                            <label>Family Type</label>
                            <input
                              type="text"
                              {...register("profile.brother")}
                              className={`form-control form-opacity border-radius-4`}
                            />
                          </div> */}
                          {/* <div className="col-md-6">
                            <label>Family Values</label>
                            <input
                              type="text"
                              {...register("profile.brother")}
                              className={`form-control form-opacity border-radius-4`}
                            />
                          </div> */}

                          <div className="col-md-6">
                            <label>Family status</label>
                            <Controller
                              control={control}
                              name="familyStatusId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.familyStatuses}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.familyStatuses &&
                                    watchedValue["profile"] &&
                                    profile.familyStatuses.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "familyStatusId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.familyStatusId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Family Values</label>
                            <Controller
                              control={control}
                              name="familyValuesId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.familyValues}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.familyValues &&
                                    watchedValue["profile"] &&
                                    profile.familyValues.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "familyValuesId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.familyValuesId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>No of Sisters</label>
                            <input
                              type="text"
                              {...register("profile.sister")}
                              className={`form-control form-opacity border-radius-0`}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Father's Occupation</label>
                            <Controller
                              control={control}
                              name="fatherOccupationId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.professions}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.professions &&
                                    watchedValue["profile"] &&
                                    profile.professions.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "fatherOccupationId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue(
                                      "profile.fatherOccupationId",
                                      val.sid
                                    );
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <label>Mother's Occupation</label>
                            <Controller
                              control={control}
                              name="motherOccupationId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={profile.professions}
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    profile.professions &&
                                    watchedValue["profile"] &&
                                    profile.professions.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"][
                                          "motherOccupationId"
                                        ]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue(
                                      "profile.motherOccupationId",
                                      val.sid
                                    );
                                  }}
                                />
                              )}
                            />
                          </div>
                          {/* <div className="col-md-6">
                            <label>Native Place</label>
                            <Controller
                              control={control}
                              name="nativePlaceId"
                              render={({ onChange, value, name, ref }) => (
                                <Select
                                  inputRef={ref}
                                  options={
                                    // getSelectedState() &&
                                    profile.states?.cities
                                  }
                                  getOptionLabel={(e) => e.name}
                                  getOptionValue={(e) => e.sid}
                                  value={
                                    watchedValue["profile"] &&
                                    // getSelectedState() &&
                                    profile.states?.cities.find(
                                      (e) =>
                                        e.sid ===
                                        watchedValue["profile"]["nativePlaceId"]
                                    )
                                  }
                                  onChange={(val) => {
                                    setValue("profile.nativePlaceId", val.sid);
                                  }}
                                />
                              )}
                            />
                          </div> */}
                        </div>
                      </>
                    )}
                    <div className="row mt-2">
                      <div className="col-md-12 justify-content-between d-flex">
                        {step !== 1 && (
                          <button
                            type="button"
                            className="btn submit"
                            onClick={prevStep}
                          >
                            Previous
                          </button>
                        )}
                        {step !== 4 && (
                          <button
                            type="button"
                            className="btn submit"
                            onClick={nextStep}
                          >
                            Save & Continue
                          </button>
                        )}
                        {step === 4 && (
                          <input
                            type="submit"
                            className="btn submit"
                            value="Save Profile"
                          />
                        )}
                      </div>
                    </div>
                  </form>
              </div>
            </div>
          </section>
        </section>
        <Footer />
      </div>
    </>
  );
}

export default UpdateProfile;
